import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, Trash2, MessageCircle, TreePine } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import MultilingualEditor from "@/components/ui/MultilingualEditor";
import MultilingualText from "@/components/ui/MultilingualText";
import PhotoDropzone from "@/components/ui/PhotoDropzone";
import { packMl, hasMlContent } from "@/lib/ml";
import { fileUrl } from "@/lib/files";
import { toast } from "sonner";

/**
 * /app/family/:id/feed — slow social timeline for a family archive.
 * Post text + optional photo URL + optional attached person. Comments per post.
 */
export default function FamilyFeed() {
  const { id } = useParams();
  const { user } = useAuth();
  const [family, setFamily] = useState(null);
  const [persons, setPersons] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Compose
  const [body, setBody] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [relatedPersonId, setRelatedPersonId] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [famRes, personsRes, feedRes] = await Promise.all([
        api.get(`/v1/families/${id}`),
        api.get(`/v1/persons`, { params: { family_id: id } }),
        api.get(`/v1/feed`, { params: { family_id: id } }),
      ]);
      setFamily(famRes.data.family);
      setPersons(personsRes.data.persons || []);
      setPosts(feedRes.data.posts || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  async function submitPost(e) {
    e.preventDefault();
    if (!hasMlContent(body)) return;
    setPosting(true);
    try {
      const { data } = await api.post("/v1/feed", {
        family_id: id,
        body: packMl(body),
        photo_url: photoUrl.trim() || undefined,
        related_person_id: relatedPersonId || undefined,
        kind: photoUrl.trim() ? "photo" : "post",});
      setPosts((p) => [data.post, ...p]);
      setBody("");
      setPhotoUrl("");
      setRelatedPersonId("");
      toast.success("Shared with the family.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPosting(false);
    }
  }

  async function comment(postId, body) {
    try {
      const { data } = await api.post("/v1/feed/comments", { post_id: postId, body });
      setPosts((ps) => ps.map((p) => (p.id === postId ? { ...p, comments: [...(p.comments || []), data.comment] } : p)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function deletePost(postId) {
    if (!confirm("Delete this post for the whole family?")) return;
    try {
      await api.delete(`/v1/feed/${postId}`);
      setPosts((ps) => ps.filter((p) => p.id !== postId));
      toast.success("Post removed.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const personById = Object.fromEntries(persons.map((p) => [p.id, p]));

  if (loading) {
    return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-[color:var(--ink-soft)]">Loading the family feed…</div>;
  }

  return (
    <section className="mx-auto max-w-3xl px-6 md:px-10 pt-8 pb-24">
      {/* Header */}
      <Link
        to={`/app/family/${id}`}
        data-testid="feed-back"
        className="inline-flex items-center gap-2 text-xs text-[color:var(--ink-soft)] link-underline mb-4"
      >
        <ArrowLeft size={12} /> Back to the tree
      </Link>

      <header className="mb-8">
        <div className="overline mb-2">§ Family Feed</div>
        <h1 data-testid="feed-family-name" className="heritage-title text-3xl md:text-5xl leading-tight">
          {family?.name}
        </h1>
        <p className="mt-2 font-italic-serif italic text-[color:var(--ink-soft)]">
          A slow, private social space. What you share here stays in the family.
        </p>
      </header>

      {/* Compose */}
      <form
        onSubmit={submitPost}
        data-testid="feed-compose"
        className="border border-[color:var(--sepia)]/25 bg-[color:var(--parchment)] p-5 mb-10 shadow-[0_10px_30px_-20px_rgba(43,28,15,0.35)]"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[color:var(--sepia)] text-[color:var(--parchment)] flex items-center justify-center font-heritage-caps text-sm shrink-0">
            {(user?.name || "?")[0].toUpperCase()}
          </div>
          <div className="flex-1">
            <MultilingualEditor
              as="textarea"
              rows={2}
              maxLength={2400}
              testid="feed-body"
              value={body}
              onChange={setBody}
              placeholder="A memory, a milestone, a photograph…"
            />
          </div>
        </div>

        <div className="mt-3">
          <PhotoDropzone value={photoUrl} onChange={setPhotoUrl} testid="feed-photo" />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[color:var(--sepia)]/20">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <select
              data-testid="feed-person-select"
              value={relatedPersonId}
              onChange={(e) => setRelatedPersonId(e.target.value)}
              className="border border-[color:var(--sepia)]/40 px-3 py-1.5 bg-transparent"
            >
              <option value="">About someone…</option>
              {persons.map((p) => (
                <option key={p.id} value={p.id}>
                  {[p.first_name, p.last_name].filter(Boolean).join(" ") || "Unnamed"}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={posting || !hasMlContent(body)}
            data-testid="feed-submit"
            className="btn-wood !py-2 !px-5 disabled:opacity-50"
          >
            <Send size={14} /> {posting ? "Sharing…" : "Share"}
          </button>
        </div>
      </form>

      {/* Timeline */}
      {posts.length === 0 ? (
        <div className="border border-dashed border-[color:var(--ink-line-strong)] p-12 text-center">
          <div className="font-italic-serif italic text-xl text-[color:var(--ink-soft)] mb-2">
            "The first page is always the hardest."
          </div>
          <p className="text-sm text-[color:var(--ink-soft)]">Share the first family memory to open the ledger.</p>
        </div>
      ) : (
        <ol className="space-y-8" data-testid="feed-timeline">
          <AnimatePresence>
            {posts.map((p) => (
              <FeedPost
                key={p.id}
                post={p}
                person={p.related_person_id ? personById[p.related_person_id] : null}
                canDelete={p.author_id === user?.id}
                onDelete={() => deletePost(p.id)}
                onComment={(body) => comment(p.id, body)}
              />
            ))}
          </AnimatePresence>
        </ol>
      )}
    </section>
  );
}

function FeedPost({ post, person, canDelete, onDelete, onComment }) {
  const [showComment, setShowComment] = useState(false);
  const [commentBody, setCommentBody] = useState("");

  async function submitComment(e) {
    e.preventDefault();
    if (!hasMlContent(commentBody)) return;
    await onComment(packMl(commentBody));
    setCommentBody("");
  }

  return (
    <motion.li
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="border-l-2 border-[color:var(--sepia)]/50 pl-5"
      data-testid={`feed-post-${post.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="overline text-[color:var(--sepia)]">
            {post.author_name} ·{" "}
            {new Date(post.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
            {post.kind && post.kind !== "post" && <span> · {post.kind}</span>}
          </div>
          {person && (
            <div className="mt-0.5 font-italic-serif italic text-[color:var(--ink-soft)] text-sm inline-flex items-center gap-1">
              <TreePine size={12} /> about {[person.first_name, person.last_name].filter(Boolean).join(" ") || "Unnamed"}
            </div>
          )}
        </div>
        {canDelete && (
          <button
            onClick={onDelete}
            data-testid={`feed-delete-${post.id}`}
            className="text-[color:var(--ink-soft)] hover:text-[color:var(--rust)] p-1"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      <MultilingualText value={post.body} className="mt-2 text-[17px] leading-[1.6] whitespace-pre-wrap" testid={`feed-body-text-${post.id}`} />

      {post.photo_url && (
        <div className="mt-4 max-w-full border border-[color:var(--sepia)]/20">
          <img
            src={fileUrl(post.photo_url)}
            alt=""
            className="w-full max-h-[520px] object-cover"
            style={{ filter: "sepia(15%)" }}
          />
        </div>
      )}

      {/* Comments */}
      <div className="mt-4 flex items-center gap-3 numeric-index text-[color:var(--ink-soft)]">
        <button
          data-testid={`feed-toggle-comments-${post.id}`}
          onClick={() => setShowComment((v) => !v)}
          className="inline-flex items-center gap-1 link-underline"
        >
          <MessageCircle size={12} /> {(post.comments || []).length}{" "}
          {(post.comments || []).length === 1 ? "comment" : "comments"}
        </button>
      </div>

      {showComment && (
        <div className="mt-3 space-y-3">
          {(post.comments || []).map((c, i) => (
            <div key={i} className="border-l border-[color:var(--sepia)]/30 pl-3 py-1">
              <div className="overline text-[color:var(--sepia)]">
                {c.name} ·{" "}
                {new Date(c.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
              </div>
              <MultilingualText value={c.body} className="mt-1 text-[15px] leading-snug font-italic-serif italic" />
            </div>
          ))}
          <form onSubmit={submitComment} className="flex gap-3 mt-3 items-end" data-testid={`feed-comment-form-${post.id}`}>
            <div className="flex-1">
              <MultilingualEditor
                as="input"
                testid={`feed-comment-${post.id}`}
                value={commentBody}
                onChange={setCommentBody}
                placeholder="Add a comment…"
                maxLength={800}
              />
            </div>
            <button
              type="submit"
              data-testid={`feed-comment-submit-${post.id}`}
              disabled={!hasMlContent(commentBody)}
              className="btn-ghost !py-1 !px-3 !text-xs disabled:opacity-50"
            >
              Send
            </button>
          </form>
        </div>
      )}
    </motion.li>
  );
}
